import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { tickValues } from '../_base';
import CustomToolTip from '../_tooltip';
import { useSelector } from 'react-redux';
import { ArrayHelpers, Copy, Forms, Loading, StringHelpers, SystemText } from '@salesperformanceint/e4shared';
import { e4Format, e4Formats, isPast } from 'helpers/dates';
import { apiPatch } from 'services';
import { SalesVelocityHelp } from '../salesVelocity';

const Chart = ({ data, item, id, reload, profileId, disableTarget }) => {
    const user = useSelector(state => state.user.user);
    const hideMeetings = useSelector(state => state.user.user.companySettings.display.hideMeetingsOnModel);

    const salesLabel = SystemText('Dashboard/Widgets/PredictiveModel/Sales', user.currencySymbol);
    const actualLabel = SystemText('Dashboard/Widgets/PredictiveModel/Actual');
    const targetLabel = SystemText('Dashboard/Widgets/PredictiveModel/Target');
    const modelLabel = SystemText('Dashboard/Widgets/PredictiveModel/Model');

    if (!data) return <Loading />;

    const current = { ...data.options };

    const saved = { ...current, ...JSON.parse(item.user_Data || '{}') };

    const [leftTicks, setLeftTicks] = React.useState([]);
    const [leftTicksExtra, setLeftTicksExtra] = React.useState({});

    const [maxY, setMaxY] = React.useState(0);
    const [cumulativeData, setCumulativeData] = React.useState([]);
    const [model, setModel] = React.useState(saved);

    React.useEffect(() => {
        if (disableTarget) {
            const temp = { ...model, target: saved.target };
            setModel(temp);
        }
    }, [saved.target]);

    React.useEffect(() => {
        let totalActual = 0;
        let totalModel = 0;
        let totalTarget = 0;

        // What's needed per additional month
        const gap = (model.target || 0) - data.data.reduce((a, c) => a + c.y, 0);
        const monthsLeft = data.data.filter(d => new Date(d.x) > new Date()).length;
        let perMonth = monthsLeft ? parseInt(gap / monthsLeft, 10) : 0;
        if (perMonth < 0) perMonth = 0;

        const temp = data.data.map(d => {
            const x = { date: d.x };
            const day = new Date(d.x);
            const daysPerMonth = 30;

            if (isPast(day)) {
                totalActual += d.y;
                totalModel += d.y;
                totalTarget += d.y;
            } else {
                totalActual += current.velocity * daysPerMonth;
                totalModel += (model.velocity || 0) * daysPerMonth;
                totalTarget += perMonth;
            }

            x[actualLabel] = totalActual;
            x[modelLabel] = totalModel;
            x[targetLabel] = totalTarget > model.target ? model.target : totalTarget;

            return x;
        });

        setCumulativeData(temp);
    }, [data, model.target, model.velocity]);

    // Set Ticks
    React.useEffect(() => {
        const me = document.querySelector(`#${id}`);
        const max = cumulativeData.reduce((a, c) => {
            let highest = a;
            if (c[actualLabel] > highest) highest = c[actualLabel];
            if (c[modelLabel] > highest) highest = c[modelLabel];
            if (c[targetLabel] > highest) highest = c[targetLabel];
            return highest;
        }, 0);
        setMaxY(max);
        let extra = {};
        setLeftTicks(tickValues([{ x: 0 }, { x: max }], 'x', me.offsetHeight, true, extra));
        setLeftTicksExtra(extra);
    }, [cumulativeData, model.target, model.velocity, item]);

    // Set Opportunities
    React.useEffect(() => {
        const temp = { ...model };
        temp.opportunities = parseInt(((model.meetings || 0) * (model.oppRate || 0)) / 100, 10);
        setModel(temp);
    }, [model.meetings, model.oppRate]);

    // Set Velocity
    React.useEffect(() => {
        const temp = { ...model };
        temp.velocity = model.adw
            ? parseInt(((model.opportunities || 0) * (model.aov || 0) * (model.winRate || 0)) / 100 / (model.adw || 0), 10)
            : 0;
        setModel(temp);
    }, [model.opportunities, model.aov, model.adw, model.winRate]);

    const doUpdate = (prop, value) => {
        let temp = { ...model };

        if (prop) {
            temp[prop] = parseInt(value, 10);
        } else {
            const target = model.target;
            temp = { ...current, target };
        }

        const values = {
            id: item.id,
            data: JSON.stringify(temp),
        };

        const url = profileId ? `profile/${profileId}/121model` : 'widget-data';

        apiPatch(url, values, user.guid, () => {
            setModel(temp);
            reload?.();
        });
    };

    // Check if each target reached
    const targetReachedActual = React.useMemo(() => {
        let month = null;
        if (!ArrayHelpers.existsAndHasLength(cumulativeData)) return 0;
        cumulativeData.forEach((d, i) => {
            if (!month && d[actualLabel] >= model.target) {
                month = d.date;
            }
        });

        return month;
    }, [cumulativeData]);

    // Check if each target reached
    const targetReachedModel = React.useMemo(() => {
        let month = null;
        if (!ArrayHelpers.existsAndHasLength(cumulativeData)) return 0;
        cumulativeData.forEach((d, i) => {
            if (!month && d[modelLabel] >= model.target) {
                month = d.date;
            }
        });

        return month;
    }, [cumulativeData]);

    // Calculate surplus earnings
    const surplus = React.useMemo(() => {
        if (!ArrayHelpers.existsAndHasLength(cumulativeData)) return 0;
        let total = cumulativeData[cumulativeData.length - 1][modelLabel];
        let extra = total - model.target;
        return extra;
    }, [cumulativeData]);

    return (
        <div className="widget-form flex" data-testid="form-predictive-widget">
            <div className="flex-50">
                <Forms.Text
                    data-testid="text-predictive-widget-target"
                    label={SystemText('Dashboard/Widgets/PredictiveModel/Fields/Target')}
                    value={model.target}
                    onChange={x => doUpdate('target', x)}
                    className="mrm10"
                    inputSuffix={
                        <span className="form-control" style={{ borderLeft: '0px', backgroundColor: '#eee' }}>
                            <b>{user.currencySymbol}</b>
                        </span>
                    }
                    type="number"
                    disabled={disableTarget}
                />
                {!hideMeetings && (
                    <>
                        <Forms.Text
                            data-testid="text-predictive-widget-meetings"
                            label={SystemText('Dashboard/Widgets/PredictiveModel/Fields/Meetings')}
                            labelFloat={`${actualLabel}: ${current.meetings}`}
                            value={model.meetings}
                            onChange={x => doUpdate('meetings', x)}
                            type="number"
                        />
                        <Forms.Text
                            data-testid="text-predictive-widget-meetingconversion"
                            label={SystemText('Dashboard/Widgets/PredictiveModel/Fields/MeetingsConversion')}
                            labelFloat={`${actualLabel}: ${current.oppRate}`}
                            value={model.oppRate}
                            className="mrm10"
                            inputSuffix={
                                <span className="form-control" style={{ borderLeft: '0px', backgroundColor: '#eee' }}>
                                    <b>%</b>
                                </span>
                            }
                            onChange={x => x >= 0 && x <= 100 && doUpdate('oppRate', x)}
                            type="number"
                        />
                    </>
                )}
                <Forms.Text
                    data-testid="text-predictive-widget-opportunities"
                    label={SystemText('Dashboard/Widgets/PredictiveModel/Fields/Opportunities')}
                    labelFloat={`${actualLabel}: ${current.opportunities}`}
                    value={model.opportunities}
                    onChange={x => doUpdate('opportunities', x)}
                    disabled={!hideMeetings}
                    type="number"
                />
                <Forms.Text
                    data-testid="text-predictive-widget-aov"
                    label={SystemText('Dashboard/Widgets/PredictiveModel/Fields/AOV')}
                    labelFloat={`${actualLabel}: ${current.aov}`}
                    value={model.aov}
                    className="mrm10"
                    inputSuffix={
                        <span className="form-control" style={{ borderLeft: '0px', backgroundColor: '#eee' }}>
                            <b>{user.currencySymbol}</b>
                        </span>
                    }
                    onChange={x => doUpdate('aov', x)}
                    type="number"
                />
                <Forms.Text
                    data-testid="text-predictive-widget-daystowin"
                    label={SystemText('Dashboard/Widgets/PredictiveModel/Fields/DaysToWin')}
                    labelFloat={`${actualLabel}: ${current.adw}`}
                    value={model.adw}
                    onChange={x => doUpdate('adw', x)}
                    type="number"
                />
                <Forms.Text
                    data-testid="text-predictive-widget-oppconversion"
                    label={SystemText('Dashboard/Widgets/PredictiveModel/Fields/OppConversion')}
                    labelFloat={`${actualLabel}: ${current.winRate}`}
                    value={model.winRate}
                    className="mrm10"
                    inputSuffix={
                        <span className="form-control" style={{ borderLeft: '0px', backgroundColor: '#eee' }}>
                            <b>%</b>
                        </span>
                    }
                    onChange={x => x >= 0 && x <= 100 && doUpdate('winRate', x)}
                    type="number"
                />
                <Forms.Text
                    data-testid="text-predictive-widget-velocity"
                    label={SystemText('Dashboard/Widgets/PredictiveModel/Fields/Velocity')}
                    labelFloat={`${actualLabel}: ${current.velocity}`}
                    value={model.velocity}
                    infoHelp={<SalesVelocityHelp />}
                    className="mrm10"
                    inputSuffix={
                        <span className="form-control" style={{ borderLeft: '0px', backgroundColor: '#eee' }}>
                            <b>{user.currencySymbol}</b>
                        </span>
                    }
                    disabled
                    type="number"
                />
                <button
                    data-testid="button-predictive-widget-reset"
                    className="btn btn-primary pull-right"
                    onClick={() => doUpdate()}
                >
                    {SystemText('Dashboard/Widgets/PredictiveModel/Buttons/Reset')}
                </button>
            </div>
            <div className="ml20 flex-50" style={{ width: '100%', height: '400px' }}>
                <div className="form-group">
                    <label>{SystemText('Dashboard/Widgets/PredictiveModel/Totals/Title')}</label>
                </div>
                <ResponsiveBar
                    data={cumulativeData}
                    keys={[actualLabel, modelLabel, targetLabel]}
                    indexBy="date"
                    margin={{ top: 20, right: 10, bottom: 50, left: 60 }}
                    padding={0.2}
                    innerPadding={2}
                    groupMode="grouped"
                    valueScale={{ type: 'linear' }}
                    indexScale={{ type: 'band', round: true }}
                    valueFormat={{ format: ' >-', enabled: false }}
                    colors={{ scheme: 'nivo' }}
                    borderRadius={2}
                    borderWidth={1}
                    borderColor={{ from: 'color', modifiers: [['darker', '0.5']] }}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        format: x => e4Format(x, e4Formats.monthVeryShort),
                    }}
                    axisLeft={{
                        tickValues: leftTicks,
                        format: x => (leftTicksExtra.format ? leftTicksExtra.format(x) : x),
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: `${salesLabel} ${leftTicksExtra.suffix ?? ''}`,
                        legendPosition: 'middle',
                        legendOffset: -50,
                    }}
                    enableLabel={false}
                    labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                    legends={[
                        {
                            dataFrom: 'keys',
                            anchor: 'top-left',
                            direction: 'column',
                            justify: false,
                            translateX: 20,
                            translateY: 10,
                            itemsSpacing: 2,
                            itemWidth: 100,
                            itemHeight: 20,
                            itemDirection: 'left-to-right',
                            itemOpacity: 1,
                            symbolSize: 20,
                            itemBackground: '#ffffff',
                            effects: [
                                {
                                    on: 'hover',
                                    style: {
                                        itemOpacity: 1,
                                    },
                                },
                            ],
                        },
                    ]}
                    tooltip={(point, x) => (
                        <CustomToolTip
                            useIndex
                            maxX={cumulativeData[cumulativeData.length - 1].date}
                            maxY={maxY}
                            point={point}
                            yFormat={y => StringHelpers.formatMetric('M', y)}
                            xFormat={x => e4Format(x, e4Formats.monthName)}
                        />
                    )}
                />
                {model.target > 0 && (
                    <div className="form-group">
                        <label>{SystemText('Dashboard/Widgets/PredictiveModel/Summary/Title')}</label>
                        <div className="text-lg p10 pt20 pb20" style={{ border: 'solid 1px #a0a0a0', borderRadius: '5px' }}>
                            <div className="flex flex-center">
                                <div className="text-center flex-10">
                                    <i
                                        className={
                                            targetReachedActual
                                                ? 'fa fa-thumbs-up text-success'
                                                : 'fa fa-thumbs-down text-danger'
                                        }
                                    />
                                </div>
                                <span className="flex-90">
                                    <Copy
                                        value={
                                            targetReachedActual
                                                ? SystemText(
                                                      'Dashboard/Widgets/PredictiveModel/Summary/Target/Actual/Reach',
                                                      e4Format(targetReachedActual, e4Formats.monthName)
                                                  )
                                                : SystemText('Dashboard/Widgets/PredictiveModel/Summary/Target/Actual/Miss')
                                        }
                                    />
                                </span>
                            </div>
                            <div className="flex flex-center mt20">
                                <div className="text-center flex-10">
                                    <i
                                        className={
                                            targetReachedModel
                                                ? 'fa fa-thumbs-up text-success'
                                                : 'fa fa-thumbs-down text-danger'
                                        }
                                    />
                                </div>
                                <span className="flex-90">
                                    <Copy
                                        value={
                                            targetReachedModel
                                                ? SystemText(
                                                      'Dashboard/Widgets/PredictiveModel/Summary/Target/Model/Reach',
                                                      e4Format(targetReachedModel, e4Formats.monthName)
                                                  )
                                                : SystemText('Dashboard/Widgets/PredictiveModel/Summary/Target/Model/Miss')
                                        }
                                    />
                                </span>
                            </div>
                            <div className="flex flex-center mt20">
                                <div className="text-center flex-10">
                                    <i
                                        className={
                                            surplus > 0
                                                ? 'fa fa-money-bill-alt text-success'
                                                : 'fa fa-money-bill-alt text-danger'
                                        }
                                    />
                                </div>
                                <span className="flex-90">
                                    <Copy
                                        value={
                                            surplus > 0
                                                ? SystemText(
                                                      'Dashboard/Widgets/PredictiveModel/Summary/Revenue/Extra',
                                                      StringHelpers.formatMetric('M', surplus, user.currencySymbol)
                                                  )
                                                : SystemText(
                                                      'Dashboard/Widgets/PredictiveModel/Summary/Revenue/Shortfall',
                                                      StringHelpers.formatMetric('M', surplus * -1, user.currencySymbol)
                                                  )
                                        }
                                    />
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Chart;
